import * as React from 'react';
import styled from 'styled-components';

import { Button, IconButton } from '@ui/shared/Button';
import { KVEntry } from '../../../utils/key_value';
import { Row } from '../../layout/Row';
import { Icon } from '../Icon';
import { ControlSpacer } from './ControlSpacer';
import { TextField } from './TextField';

interface IKeyValueControlProps {
  kvEntries: KVEntry[];
  onChange: (entries: KVEntry[]) => void;
  excludeProperties?: string[];
}

export const KeyValueControl = (
  props: IKeyValueControlProps
): React.ReactElement => {
  const addRowHandler = (): void => {
    const copyOfEntries = props.kvEntries.slice(0);
    copyOfEntries.push([copyOfEntries.length, '', '']);
    props.onChange(copyOfEntries);
  };

  const removeRowHandler = (index: number): void => {
    const copyOfEntries = props.kvEntries.slice(0);
    copyOfEntries.splice(index, 1);
    props.onChange(copyOfEntries);
  };

  /**
   * @description handles changes to key/value inputs
   * @param index
   * @param key
   * @param value
   */
  const valuesChangeHandler = (
    index: number,
    key: string,
    value: string | number
  ): void => {
    const copyOfEntries = props.kvEntries.slice(0);
    const row = copyOfEntries.find((v: KVEntry): boolean => index === v[0]);
    row[1] = key;
    row[2] = value.toString();
    copyOfEntries.splice(index, 1, row);
    props.onChange(copyOfEntries);
  };

  return (
    <Container>
      <h5>External Properties</h5>
      {props.kvEntries.map((entry: KVEntry): React.ReactElement => {
        return (
          <ControlSpacer key={entry[0]}>
            <ControlRow
              onRemove={removeRowHandler}
              index={entry[0]}
              keyName={entry[1]}
              value={entry[2]}
              onChange={valuesChangeHandler}
            />
          </ControlSpacer>
        );
      })}

      <ActionRow>
        <Button title='Add Property' icon='add' onClick={addRowHandler} />
      </ActionRow>
    </Container>
  );
};

const Container = styled.div`
  border-bottom: 1px;
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ActionRow = styled(Row as any)`
  margin-top: 10px;
`;

interface IControlRowProps {
  index: number;
  onChange: (index: number, key: string, value: string | number) => void;
  onRemove: (index: number) => void;
  keyName: string | null;
  value: string | number | null;
}
const ControlRow = (props: IControlRowProps): React.ReactElement => {
  const removeRow = (): void => props.onRemove(props.index);

  return (
    <Row
      style={{
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <ControlContainer>
        <TextField
          style={{ width: '100%' }}
          name={`key-${props.index.toString()}`}
          onChange={(val: string): void =>
            props.onChange(props.index, val, props.value)
          }
          value={props.keyName ?? ''}
          label='Name'
          placeholder='Name...'
          type='text'
        />
      </ControlContainer>

      <ControlContainer>
        <TextField
          name={`value-${props.index.toString()}`}
          onChange={(val: string): void =>
            props.onChange(props.index, props.keyName, val)
          }
          value={props.value ?? ''}
          label='Value'
          placeholder='Value...'
          type='text'
        />
      </ControlContainer>

      <ClearContainer>
        <IconButton icon='clear' title='Remove Property' onClick={removeRow} />
      </ClearContainer>
    </Row>
  );
};

const ClearContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: flex-end;
  margin-top: 24px;
  margin-left: 10px;
  margin-right: 10px;
`;

const ControlContainer = styled.div`
  flex: 1;
  margin-right: 10px;
`;
